import { type Device } from "@/lib/api";
import { relativeTime } from "@/lib/time";

interface DeviceCardProps {
  device: Device;
}

export function DeviceCard({ device }: DeviceCardProps) {
  const isOnline = device.status === "online";

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors animate-fade-in">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
            {device.role === "server" ? (
              <svg
                className="w-5 h-5 text-blue-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-purple-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z"
                />
              </svg>
            )}
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-zinc-100 truncate">
              {device.name}
            </h3>
            <p className="text-sm text-zinc-500">
              {device.model} &middot;{" "}
              <span className="capitalize">{device.role}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {device.updateAvailable && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
              v{device.updateAvailable}
            </span>
          )}
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              isOnline
                ? "bg-green-500/10 text-green-400"
                : "bg-zinc-800 text-zinc-500"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isOnline ? "bg-green-500 animate-pulse-dot" : "bg-zinc-600"
              }`}
            />
            {isOnline ? "Online" : "Offline"}
          </span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <p className="text-xs text-zinc-500 mb-0.5">Device ID</p>
          <p className="text-sm font-mono text-zinc-300 truncate">
            {device.id}
          </p>
        </div>
        <div>
          <p className="text-xs text-zinc-500 mb-0.5">Version</p>
          <p className="text-sm font-mono text-zinc-300">
            v{device.currentVersion}
          </p>
        </div>
        <div>
          <p className="text-xs text-zinc-500 mb-0.5">Channel</p>
          <p className="text-sm text-zinc-300 capitalize">{device.channel}</p>
        </div>
        <div>
          <p className="text-xs text-zinc-500 mb-0.5">Last seen</p>
          <p className="text-sm text-zinc-300">
            {isOnline ? "Now" : relativeTime(device.lastSeen)}
          </p>
        </div>
      </div>
    </div>
  );
}
